<template>
  <div class="mt-6 border rounded-lg bg-white p-4">
    <div class="flex items-center justify-between gap-3 mb-1">
      <h3 class="text-lg font-semibold">Benchmark overview (admin)</h3>
      <button
        type="button"
        class="px-3 py-1.5 rounded border text-xs bg-white hover:bg-gray-50"
        :disabled="loading"
        @click="$emit('refresh')"
      >
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </div>
    <div class="text-xs text-gray-500 mb-3">
      Rolling 12-month benchmark across your accessible universities.
      <span v-if="asOfDate">As of {{ asOfDate }}.</span>
    </div>
    <div v-if="loading" class="text-sm text-gray-500">Loading benchmark overview…</div>
    <div v-else-if="error" class="text-sm text-red-600">{{ error }}</div>
    <div v-else-if="!rows.length" class="text-sm text-gray-500">No benchmark rows available.</div>
    <div v-else class="overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead>
          <tr class="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
            <th class="py-2 pr-3">University</th>
            <th class="py-2 pr-3">Graduate Amplification Index</th>
            <th class="py-2 pr-3">Views Generated per Share</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.university_slug"
            class="border-b last:border-b-0 align-top"
          >
            <td class="py-2 pr-3">
              <div class="font-medium text-gray-900">{{ universityName(row.university_slug) }}</div>
              <div class="text-xs text-gray-500">{{ row.university_slug }}</div>
            </td>
            <td class="py-2 pr-3">
              <div class="text-gray-900">{{ metricLabel(row, 'graduate_amplification_index') }}</div>
              <div class="text-xs text-gray-500">{{ percentileLabel(row, 'graduate_amplification_index') }}</div>
            </td>
            <td class="py-2 pr-3">
              <div class="text-gray-900">{{ metricLabel(row, 'observed_share_amplification') }}</div>
              <div class="text-xs text-gray-500">{{ percentileLabel(row, 'observed_share_amplification') }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminBenchmarkOverviewSection',
  props: {
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    overview: { type: Object, default: null },
    universityNameBySlug: { type: Object, default: () => ({}) },
  },
  computed: {
    asOfDate() {
      const value = String(this.overview?.as_of_date || '').trim()
      return value || ''
    },
    rows() {
      const universities = Array.isArray(this.overview?.universities)
        ? this.overview.universities
        : []
      const metricPct = (row, metricKey) => Number(row?.metrics?.[metricKey]?.percentile || 0)
      return [...universities].sort((a, b) => {
        const pctDiff = metricPct(b, 'graduate_amplification_index') - metricPct(a, 'graduate_amplification_index')
        if (pctDiff !== 0) return pctDiff
        return String(a?.university_slug || '').localeCompare(String(b?.university_slug || ''))
      })
    },
  },
  methods: {
    universityName(slug) {
      const key = String(slug || '')
      const mapped = String(this.universityNameBySlug?.[key] || '').trim()
      return mapped || key
    },
    metricRow(row, metricKey) {
      return row?.metrics?.[metricKey] || null
    },
    metricLabel(row, metricKey) {
      const metric = this.metricRow(row, metricKey)
      const band = String(metric?.label || '').trim()
      if (!band) return 'N/A'
      return band
    },
    percentileLabel(row, metricKey) {
      const metric = this.metricRow(row, metricKey)
      if (!metric) return 'No benchmark value'
      const percentile = Number(metric?.percentile)
      const value = Number(metric?.value)
      const hasPercentile = Number.isFinite(percentile)
      const hasValue = Number.isFinite(value)
      const valueSuffix = metricKey === 'graduate_amplification_index' ? 'x' : ''
      if (hasPercentile && hasValue) return `${percentile.toFixed(1)}th percentile • value ${value.toFixed(2)}${valueSuffix}`
      if (hasPercentile) return `${percentile.toFixed(1)}th percentile`
      if (hasValue) return `value ${value.toFixed(2)}${valueSuffix}`
      return 'No benchmark value'
    },
  },
}
</script>
